import { useEffect } from 'react';
import type { CopilotSuggestion } from '../api/mediatorService';
import type { Party } from '../types/case';
import { SuggestionCard } from './SuggestionCard';

interface Props {
  suggestions: CopilotSuggestion[];
  parties: Party[];
  onDismiss: (id: string) => void;
  onPin: (id: string) => void;
  /**
   * Controlled open state. The parent owns this so it can hide the transcript
   * while the tray is expanded (mutually exclusive views).
   */
  open: boolean;
  onToggle: (open: boolean) => void;
}

/**
 * Copilot suggestions tray — when open, takes the full sidebar height and
 * scrolls internally; when closed, collapses to its header strip. Auto-opens
 * when a new high-priority suggestion lands so the mediator can't miss it.
 */
export function SuggestionsTray({ suggestions, parties, onDismiss, onPin, open, onToggle }: Props) {
  const hasHigh = suggestions.some((s) => s.priority === 'high');

  useEffect(() => {
    if (hasHigh && !open) onToggle(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasHigh]);

  if (suggestions.length === 0) return null;

  const headerTone = hasHigh
    ? 'bg-red-50 text-red-900'
    : suggestions.some((s) => s.priority === 'medium')
      ? 'bg-amber-50 text-amber-900'
      : 'bg-gray-50 text-gray-800';

  // When the tray is open it claims all of the available vertical space and
  // scrolls internally; when closed it collapses to a header strip.
  const sectionClass = open
    ? 'flex min-h-0 flex-1 flex-col border-b border-surface-border'
    : 'flex shrink-0 flex-col border-b border-surface-border';

  return (
    <section className={sectionClass}>
      <button
        type="button"
        onClick={() => onToggle(!open)}
        aria-expanded={open}
        className={`flex shrink-0 w-full items-center justify-between px-3 py-1.5 text-xs font-medium ${headerTone}`}
      >
        <span className="flex items-center gap-2">
          <span aria-hidden>{open ? '▾' : '▸'}</span>
          <span>
            💡 {suggestions.length} copilot{' '}
            {suggestions.length === 1 ? 'suggestion' : 'suggestions'}
          </span>
          {hasHigh && <span className="animate-pulse text-red-600">●</span>}
        </span>
        <span className="text-[11px] uppercase tracking-wider opacity-70">
          {open ? 'hide' : 'show'}
        </span>
      </button>

      {open && (
        <div className="min-h-0 flex-1 space-y-2 overflow-y-auto bg-surface-muted p-2">
          {suggestions.map((s) => (
            <SuggestionCard
              key={s.id}
              suggestion={s}
              parties={parties}
              onDismiss={onDismiss}
              onPin={onPin}
            />
          ))}
        </div>
      )}
    </section>
  );
}
